// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: per-user calendar preferences (week start, shown calendars, auto
// presence), stored server-side so they follow the user across devices.

import {
	DEFAULT_MOBILE_RAIL_TABS,
	defaultMobileMoreApps,
	type MobileMoreAppId,
	type MobileRailTabId,
	sanitizeMobileMoreApps,
	sanitizeMobileRailTabs,
} from '@app/features/app/components/layout/mobile_rail/MobileRailTypes';
import {
	DEFAULT_DESKTOP_SIDEBAR,
	type DesktopSidebarPrefs,
	sanitizeDesktopSidebar,
} from '@app/features/app/components/layout/sidebar_nav/SidebarPages';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import {
	type CalendarLayers,
	DEFAULT_CALENDAR_LAYERS,
	sanitizeCalendarLayers,
} from '@app/features/konvo_calendar/utils/KonvoCalendarLayers';
import {type MailChatHideRule, sanitizeRules} from '@app/features/konvo_mail/utils/MailChatHiding';
import {type MailFyiRule, sanitizeMailFyiRules} from '@app/features/konvo_mail/utils/MailFyi';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {makeAutoObservable, runInAction} from 'mobx';

export type KonvoWeekStart = 'monday' | 'sunday';

export interface KonvoOutlookCalendar {
	id: string;
	name: string;
	hex_color: string | null;
	is_default: boolean;
	can_edit: boolean;
	owner: string | null;
}

interface PrefsPayload {
	week_start: KonvoWeekStart;
	auto_presence: boolean;
	meeting_reminders?: boolean;
	daily_agenda?: boolean;
	weekly_time_summary?: boolean;
	end_of_day_digest?: boolean;
	monday_report?: boolean;
	agents_digest?: boolean;
	calendar_ids: Array<string> | null;
	calendar_colors?: Record<string, string>;
	weather_location?: WeatherPlace | null;
	rail_auto_hide?: boolean;
	rail_auto_hide_delay?: number;
	/** Phone rail tabs, in order. Ask is always present and is not listed. */
	mobile_rail_tabs?: Array<string>;
	/** Apps shown as tiles in the phone rail's More panel, in order. */
	mobile_more_apps?: Array<string>;
	/** Desktop sidebar page buttons: every page id in order, and the ones switched off. */
	desktop_sidebar?: {order?: Array<string>; hidden?: Array<string>};
	/** Mail hidden from the chat lists (still in Mail), by sender address or subject phrase. */
	mail_chat_hide?: Array<{kind: string; value: string}>;
	/** FYI mail choices by group ("Always tidy", "Not FYI"); changed through their own route, never PUT here. */
	mail_fyi_rules?: Record<string, string>;
	/** What the Calendar draws: meetings, Scheduled items, ClickUp due dates, and their finer choices. */
	calendar_layers?: Partial<CalendarLayers>;
}

export interface WeatherPlace {
	lat: number;
	lon: number;
	label: string;
}

const RAIL_CACHE_KEY = 'konvo_rail.auto_hide';

function readRailCache(): {autoHide: boolean; delay: number} {
	try {
		const raw = getProtectedLocalStorage()?.getItem(RAIL_CACHE_KEY);
		if (!raw) return {autoHide: false, delay: 5};
		const parsed = JSON.parse(raw) as {autoHide?: boolean; delay?: number};
		return {autoHide: parsed.autoHide === true, delay: typeof parsed.delay === 'number' ? parsed.delay : 5};
	} catch {
		return {autoHide: false, delay: 5};
	}
}

function writeRailCache(autoHide: boolean, delay: number): void {
	try {
		getProtectedLocalStorage()?.setItem(RAIL_CACHE_KEY, JSON.stringify({autoHide, delay}));
	} catch {
		// storage unavailable
	}
}

const MOBILE_RAIL_CACHE_KEY = 'konvo_rail.mobile_tabs';

function readMobileRailCache(): Array<MobileRailTabId> {
	try {
		const raw = getProtectedLocalStorage()?.getItem(MOBILE_RAIL_CACHE_KEY) ?? null;
		if (!raw) return [...DEFAULT_MOBILE_RAIL_TABS];
		return sanitizeMobileRailTabs(JSON.parse(raw));
	} catch {
		return [...DEFAULT_MOBILE_RAIL_TABS];
	}
}

function writeMobileRailCache(tabs: Array<MobileRailTabId>): void {
	try {
		getProtectedLocalStorage()?.setItem(MOBILE_RAIL_CACHE_KEY, JSON.stringify(tabs));
	} catch {
		// storage unavailable
	}
}

const MOBILE_MORE_APPS_CACHE_KEY = 'konvo_rail.mobile_more_apps';

function readMobileMoreAppsCache(tabs: ReadonlyArray<MobileRailTabId>): Array<MobileMoreAppId> {
	try {
		const raw = getProtectedLocalStorage()?.getItem(MOBILE_MORE_APPS_CACHE_KEY) ?? null;
		if (!raw) return defaultMobileMoreApps(tabs);
		return sanitizeMobileMoreApps(JSON.parse(raw), tabs);
	} catch {
		return defaultMobileMoreApps(tabs);
	}
}

function writeMobileMoreAppsCache(apps: Array<MobileMoreAppId>): void {
	try {
		getProtectedLocalStorage()?.setItem(MOBILE_MORE_APPS_CACHE_KEY, JSON.stringify(apps));
	} catch {
		// storage unavailable
	}
}

const DESKTOP_SIDEBAR_CACHE_KEY = 'konvo_sidebar.pages';

function readDesktopSidebarCache(): DesktopSidebarPrefs {
	try {
		const raw = getProtectedLocalStorage()?.getItem(DESKTOP_SIDEBAR_CACHE_KEY) ?? null;
		if (!raw) return sanitizeDesktopSidebar(DEFAULT_DESKTOP_SIDEBAR);
		return sanitizeDesktopSidebar(JSON.parse(raw));
	} catch {
		return sanitizeDesktopSidebar(DEFAULT_DESKTOP_SIDEBAR);
	}
}

function writeDesktopSidebarCache(prefs: DesktopSidebarPrefs): void {
	try {
		getProtectedLocalStorage()?.setItem(DESKTOP_SIDEBAR_CACHE_KEY, JSON.stringify(prefs));
	} catch {
		// storage unavailable
	}
}

const MAIL_CHAT_HIDE_CACHE_KEY = 'konvo_mail.chat_hide';

function readMailChatHideCache(): Array<MailChatHideRule> {
	try {
		const raw = getProtectedLocalStorage()?.getItem(MAIL_CHAT_HIDE_CACHE_KEY) ?? null;
		return raw ? sanitizeRules(JSON.parse(raw)) : [];
	} catch {
		return [];
	}
}

function writeMailChatHideCache(rules: Array<MailChatHideRule>): void {
	try {
		getProtectedLocalStorage()?.setItem(MAIL_CHAT_HIDE_CACHE_KEY, JSON.stringify(rules));
	} catch {
		// storage unavailable
	}
}

const CALENDAR_LAYERS_CACHE_KEY = 'konvo_calendar.layers';

function readCalendarLayersCache(): CalendarLayers {
	try {
		const raw = getProtectedLocalStorage()?.getItem(CALENDAR_LAYERS_CACHE_KEY) ?? null;
		return raw ? sanitizeCalendarLayers(JSON.parse(raw)) : {...DEFAULT_CALENDAR_LAYERS};
	} catch {
		return {...DEFAULT_CALENDAR_LAYERS};
	}
}

function writeCalendarLayersCache(layers: CalendarLayers): void {
	try {
		getProtectedLocalStorage()?.setItem(CALENDAR_LAYERS_CACHE_KEY, JSON.stringify(layers));
	} catch {
		// storage unavailable
	}
}

class KonvoCalendarPrefsStore {
	weekStart: KonvoWeekStart = 'monday';
	autoPresence = true;
	meetingReminders = true;
	dailyAgenda = true;
	weeklyTimeSummary = true;
	endOfDayDigest = false;
	mondayReport = true;
	/** Weekday 07:30 agents digest in the Assistant chat (on unless switched off). */
	agentsDigest = true;
	/** null = default calendar only (the pre-multi-calendar behaviour). */
	calendarIds: Array<string> | null = null;
	/** Per-calendar colour overrides (calendar id -> #rrggbb). */
	calendarColors: Record<string, string> = {};
	/** Today page weather place, synced across devices. undefined = not loaded yet. */
	weatherLocation: WeatherPlace | null | undefined = undefined;
	/** Desktop Rail: collapse to a chevron and hide after a delay. Cached locally so the Rail starts right. */
	railAutoHide = readRailCache().autoHide;
	railAutoHideDelay = readRailCache().delay;
	/** Phone rail tabs in order (max 5; Ask is implicit). Cached locally so the rail draws right at boot. */
	mobileRailTabs: Array<MobileRailTabId> = readMobileRailCache();
	/** More-panel app tiles in order (max 4; never duplicates a rail tab). */
	mobileMoreApps: Array<MobileMoreAppId> = readMobileMoreAppsCache(this.mobileRailTabs);
	/** Desktop sidebar page order and hidden pages. Cached locally so the sidebar draws right at boot. */
	desktopSidebar: DesktopSidebarPrefs = readDesktopSidebarCache();
	/** Mail hidden from the chat lists. Cached locally so Chats draws without them at boot. */
	mailChatHide: Array<MailChatHideRule> = readMailChatHideCache();
	mailFyiRules: Record<string, MailFyiRule> = {};
	/** What the Calendar draws. Cached locally so the Calendar draws them at once. */
	calendarLayers: CalendarLayers = readCalendarLayersCache();
	calendars: Array<KonvoOutlookCalendar> = [];
	loaded = false;
	private loadPromise: Promise<void> | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	load(): Promise<void> {
		if (!this.loadPromise) {
			this.loadPromise = this.fetchAll().finally(() => {
				runInAction(() => {
					this.loaded = true;
				});
			});
		}
		return this.loadPromise;
	}

	async refreshCalendars(): Promise<void> {
		try {
			const response = await http.get<{connected: boolean; calendars: Array<KonvoOutlookCalendar>}>(
				Endpoints.KONVO_CALENDARS,
			);
			runInAction(() => {
				this.calendars = response.body?.calendars ?? [];
			});
		} catch {
			// leave the previous list
		}
	}

	private async fetchAll(): Promise<void> {
		try {
			const response = await http.get<PrefsPayload>(Endpoints.KONVO_CALENDAR_PREFS);
			const prefs = response.body;
			runInAction(() => {
				this.weekStart = prefs?.week_start === 'sunday' ? 'sunday' : 'monday';
				this.autoPresence = prefs?.auto_presence !== false;
				this.meetingReminders = prefs?.meeting_reminders !== false;
				this.dailyAgenda = prefs?.daily_agenda !== false;
				this.weeklyTimeSummary = prefs?.weekly_time_summary !== false;
				this.endOfDayDigest = prefs?.end_of_day_digest === true;
				this.mondayReport = prefs?.monday_report !== false;
				this.agentsDigest = prefs?.agents_digest !== false;
				this.calendarIds = prefs?.calendar_ids ?? null;
				this.calendarColors = prefs?.calendar_colors ?? {};
				this.weatherLocation = prefs?.weather_location ?? null;
				this.railAutoHide = prefs?.rail_auto_hide === true;
				this.railAutoHideDelay = prefs?.rail_auto_hide_delay ?? 5;
				this.mobileRailTabs = sanitizeMobileRailTabs(prefs?.mobile_rail_tabs);
				this.mobileMoreApps = sanitizeMobileMoreApps(prefs?.mobile_more_apps, this.mobileRailTabs);
				if (prefs?.desktop_sidebar !== undefined) this.desktopSidebar = sanitizeDesktopSidebar(prefs.desktop_sidebar);
				if (prefs?.mail_chat_hide !== undefined) this.mailChatHide = sanitizeRules(prefs.mail_chat_hide);
				if (prefs?.mail_fyi_rules !== undefined) this.mailFyiRules = sanitizeMailFyiRules(prefs.mail_fyi_rules);
				if (prefs?.calendar_layers !== undefined) this.calendarLayers = sanitizeCalendarLayers(prefs.calendar_layers);
				writeMailChatHideCache(this.mailChatHide);
				writeCalendarLayersCache(this.calendarLayers);
				writeRailCache(this.railAutoHide, this.railAutoHideDelay);
				writeMobileRailCache(this.mobileRailTabs);
				writeMobileMoreAppsCache(this.mobileMoreApps);
				writeDesktopSidebarCache(this.desktopSidebar);
			});
		} catch {
			// defaults stand
		}
		await this.refreshCalendars();
	}

	/** A KONVO_PREFS_UPDATE from another window or device. */
	applyRemote(prefs: PrefsPayload): void {
		runInAction(() => {
			this.weekStart = prefs.week_start === 'sunday' ? 'sunday' : 'monday';
			this.autoPresence = prefs.auto_presence !== false;
			this.meetingReminders = prefs.meeting_reminders !== false;
			this.dailyAgenda = prefs.daily_agenda !== false;
			this.weeklyTimeSummary = prefs.weekly_time_summary !== false;
			this.endOfDayDigest = prefs.end_of_day_digest === true;
			this.mondayReport = prefs.monday_report !== false;
			this.agentsDigest = prefs.agents_digest !== false;
			this.calendarIds = prefs.calendar_ids ?? null;
			this.calendarColors = prefs.calendar_colors ?? {};
			this.weatherLocation = prefs.weather_location ?? null;
			this.railAutoHide = prefs.rail_auto_hide === true;
			this.railAutoHideDelay = prefs.rail_auto_hide_delay ?? 5;
			this.mobileRailTabs = sanitizeMobileRailTabs(prefs.mobile_rail_tabs);
			this.mobileMoreApps = sanitizeMobileMoreApps(prefs.mobile_more_apps, this.mobileRailTabs);
			// Servers older than the sidebar setting do not send it; keep what this device has.
			if (prefs.desktop_sidebar !== undefined) this.desktopSidebar = sanitizeDesktopSidebar(prefs.desktop_sidebar);
			if (prefs.mail_chat_hide !== undefined) this.mailChatHide = sanitizeRules(prefs.mail_chat_hide);
			if (prefs.mail_fyi_rules !== undefined) this.mailFyiRules = sanitizeMailFyiRules(prefs.mail_fyi_rules);
			if (prefs.calendar_layers !== undefined) this.calendarLayers = sanitizeCalendarLayers(prefs.calendar_layers);
			writeMailChatHideCache(this.mailChatHide);
			writeCalendarLayersCache(this.calendarLayers);
			writeRailCache(this.railAutoHide, this.railAutoHideDelay);
			writeMobileRailCache(this.mobileRailTabs);
			writeMobileMoreAppsCache(this.mobileMoreApps);
			writeDesktopSidebarCache(this.desktopSidebar);
		});
	}

	async save(
		partial: Partial<{
			weekStart: KonvoWeekStart;
			autoPresence: boolean;
			meetingReminders: boolean;
			dailyAgenda: boolean;
			weeklyTimeSummary: boolean;
			endOfDayDigest: boolean;
			mondayReport: boolean;
			agentsDigest: boolean;
			calendarIds: Array<string> | null;
			calendarColors: Record<string, string>;
			weatherLocation: WeatherPlace | null;
			railAutoHide: boolean;
			railAutoHideDelay: number;
			mobileRailTabs: Array<MobileRailTabId>;
			mobileMoreApps: Array<MobileMoreAppId>;
			desktopSidebar: DesktopSidebarPrefs;
			mailChatHide: Array<MailChatHideRule>;
			calendarLayers: CalendarLayers;
		}>,
	) {
		// The PUT sends every field, so make sure the stored values have been
		// fetched first or a fresh device would overwrite them with its cache.
		if (this.loadPromise) await this.loadPromise;
		runInAction(() => {
			if (partial.weekStart !== undefined) this.weekStart = partial.weekStart;
			if (partial.autoPresence !== undefined) this.autoPresence = partial.autoPresence;
			if (partial.meetingReminders !== undefined) this.meetingReminders = partial.meetingReminders;
			if (partial.dailyAgenda !== undefined) this.dailyAgenda = partial.dailyAgenda;
			if (partial.weeklyTimeSummary !== undefined) this.weeklyTimeSummary = partial.weeklyTimeSummary;
			if (partial.endOfDayDigest !== undefined) this.endOfDayDigest = partial.endOfDayDigest;
			if (partial.mondayReport !== undefined) this.mondayReport = partial.mondayReport;
			if (partial.agentsDigest !== undefined) this.agentsDigest = partial.agentsDigest;
			if (partial.calendarIds !== undefined) this.calendarIds = partial.calendarIds;
			if (partial.calendarColors !== undefined) this.calendarColors = partial.calendarColors;
			if (partial.weatherLocation !== undefined) this.weatherLocation = partial.weatherLocation;
			if (partial.railAutoHide !== undefined) this.railAutoHide = partial.railAutoHide;
			if (partial.railAutoHideDelay !== undefined) this.railAutoHideDelay = partial.railAutoHideDelay;
			if (partial.mobileRailTabs !== undefined) this.mobileRailTabs = sanitizeMobileRailTabs(partial.mobileRailTabs);
			if (partial.mobileMoreApps !== undefined) {
				this.mobileMoreApps = sanitizeMobileMoreApps(partial.mobileMoreApps, this.mobileRailTabs);
			} else if (partial.mobileRailTabs !== undefined) {
				this.mobileMoreApps = sanitizeMobileMoreApps(this.mobileMoreApps, this.mobileRailTabs);
			}
			if (partial.desktopSidebar !== undefined) this.desktopSidebar = sanitizeDesktopSidebar(partial.desktopSidebar);
			if (partial.mailChatHide !== undefined) this.mailChatHide = sanitizeRules(partial.mailChatHide);
			if (partial.calendarLayers !== undefined) this.calendarLayers = sanitizeCalendarLayers(partial.calendarLayers);
			writeMailChatHideCache(this.mailChatHide);
			writeCalendarLayersCache(this.calendarLayers);
			writeRailCache(this.railAutoHide, this.railAutoHideDelay);
			writeMobileRailCache(this.mobileRailTabs);
			writeMobileMoreAppsCache(this.mobileMoreApps);
			writeDesktopSidebarCache(this.desktopSidebar);
		});
		try {
			await http.put(Endpoints.KONVO_CALENDAR_PREFS, {
				body: {
					week_start: this.weekStart,
					auto_presence: this.autoPresence,
					meeting_reminders: this.meetingReminders,
					daily_agenda: this.dailyAgenda,
					weekly_time_summary: this.weeklyTimeSummary,
					end_of_day_digest: this.endOfDayDigest,
					monday_report: this.mondayReport,
					agents_digest: this.agentsDigest,
					calendar_ids: this.calendarIds,
					calendar_colors: this.calendarColors,
					weather_location: this.weatherLocation ?? null,
					rail_auto_hide: this.railAutoHide,
					rail_auto_hide_delay: this.railAutoHideDelay,
					mobile_rail_tabs: this.mobileRailTabs,
					mobile_more_apps: this.mobileMoreApps,
					desktop_sidebar: this.desktopSidebar,
					mail_chat_hide: this.mailChatHide,
					calendar_layers: this.calendarLayers,
				},
			});
		} catch {
			// keep optimistic local state; next save retries
		}
	}

	/**
	 * "Always tidy" or "Not FYI" for one FYI mail group (null: back to the FYI card). Saved
	 * through its own route so a stale window's full PUT can never undo it.
	 */
	async setMailFyiRule(group: string, rule: MailFyiRule | null): Promise<boolean> {
		const before = this.mailFyiRules;
		const next = {...before};
		if (rule) next[group] = rule;
		else delete next[group];
		this.mailFyiRules = next;
		try {
			const response = await http.put<{rules?: Record<string, string>}>(Endpoints.KONVO_MAIL_FYI_RULE(group), {
				body: {rule},
			});
			if (response.body?.rules) {
				const rules = response.body.rules;
				runInAction(() => {
					this.mailFyiRules = sanitizeMailFyiRules(rules);
				});
			}
			return true;
		} catch {
			runInAction(() => {
				this.mailFyiRules = before;
			});
			return false;
		}
	}

	/** Change what the Calendar draws; saved for the person across devices. */
	setCalendarLayers(change: Partial<CalendarLayers>): void {
		void this.save({calendarLayers: {...this.calendarLayers, ...change}});
	}

	/** Outlook calendars the Calendar asks for. No choice yet means the default calendar only. */
	get shownCalendarIds(): Array<string> {
		if (this.calendarIds !== null) return this.calendarIds;
		return this.calendars.filter((calendar) => calendar.is_default).map((calendar) => calendar.id);
	}

	isCalendarShown(id: string): boolean {
		return this.shownCalendarIds.includes(id);
	}

	/** Show or hide one Outlook calendar. The caller refreshes the events once this resolves. */
	setCalendarShown(id: string, shown: boolean): Promise<void> {
		const without = this.shownCalendarIds.filter((entry) => entry !== id);
		return this.save({calendarIds: shown ? [...without, id] : without});
	}

	/** Show this Outlook calendar alone. The caller refreshes the events once this resolves. */
	showOnlyCalendar(id: string): Promise<void> {
		return this.save({calendarIds: [id]});
	}

	calendarById(id: string | null): KonvoOutlookCalendar | null {
		if (!id) return null;
		return this.calendars.find((calendar) => calendar.id === id) ?? null;
	}

	/** Colour override first, then the calendar's own Outlook colour. */
	colorFor(id: string | null): string | null {
		if (!id) return null;
		return this.calendarColors[id] ?? this.calendarById(id)?.hex_color ?? null;
	}
}

export default new KonvoCalendarPrefsStore();
